import { CaretDown, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { format } from 'date-fns';
import { css, html } from 'react-strict-dom';
import { Color, Style } from 'src/constants';
import { color } from 'src/constants/color.css';

import DropdownMenu from '../../../components/DropdownMenu';
import DropdownMenuItem from '../../../components/DropdownMenuItem';
import Text from '../../../components/Text';

export type ReportMode = 'month' | 'week';

type Props = {
  mode: ReportMode;
  /** The first day of the period shown. */
  period: Date;
  periods: Array<Date>;
  /** True while the current calendar period is on screen. */
  canGoNext: boolean;
  /** False when there is no earlier period to move to. */
  canGoPrev?: boolean;
  /** "month to date" / "week to date" while the current period is on screen. */
  hint: string | null;
  onNavigate: (delta: -1 | 1) => void;
  onSelect: (period: Date) => void;
};

function label(period: Date, mode: ReportMode): string {
  return mode === 'month'
    ? format(period, 'MMMM y')
    : format(period, 'd MMM y');
}

/**
 * Switches the report between periods - a month or a week.  The label carries
 * the period, so the same part serves both.  The picker lists twelve periods
 * back, newest first, and the next arrow is disabled once the current
 * calendar period is reached.
 */
export default function ReportPeriodSwitcher({
  mode,
  period,
  periods,
  canGoNext,
  canGoPrev = true,
  hint,
  onNavigate,
  onSelect,
}: Props) {
  return (
    <html.div style={styles.container}>
      <html.button
        style={[
          Style.centerBoth,
          styles.square,
          styles.button,
          !canGoPrev && styles.disabled,
        ]}
        onClick={canGoPrev ? () => onNavigate(-1) : undefined}
        disabled={!canGoPrev}
        aria-label="Previous period"
      >
        <CaretLeft
          size={14}
          color={canGoPrev ? Color.neutral80 : Color.neutral40}
        />
      </html.button>

      <DropdownMenu
        trigger={(open) => (
          <html.button
            style={[
              Style.row,
              Style.alignCenter,
              styles.select,
              styles.button,
              open && styles.selectOpen,
            ]}
          >
            <Text typeface="body.small.medium" color="neutral100">
              {label(period, mode)}
            </Text>
            <CaretDown size={14} color={Color.neutral80} />
          </html.button>
        )}
      >
        {(close) =>
          periods.map((option) => (
            <DropdownMenuItem
              key={option.toISOString()}
              showRadio={false}
              selected={option.getTime() === period.getTime()}
              onClick={() => {
                onSelect(option);
                close();
              }}
            >
              {label(option, mode)}
            </DropdownMenuItem>
          ))
        }
      </DropdownMenu>

      <html.button
        style={[
          Style.centerBoth,
          styles.square,
          styles.button,
          !canGoNext && styles.disabled,
        ]}
        onClick={canGoNext ? () => onNavigate(1) : undefined}
        disabled={!canGoNext}
        aria-label="Next period"
      >
        <CaretRight
          size={14}
          color={canGoNext ? Color.neutral80 : Color.neutral40}
        />
      </html.button>

      <html.div style={styles.spacer} />
      {hint && (
        <Text typeface="caption.medium" color="neutral40">
          {hint}
        </Text>
      )}
    </html.div>
  );
}

const styles = css.create({
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  square: {
    width: 34,
    height: 34,
    borderRadius: 8,
    flexShrink: 0,
  },
  button: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    backgroundColor: color.neutral0,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    ':hover': {
      backgroundColor: color.neutral5,
    },
  },
  select: {
    gap: 6,
    paddingBlock: 6,
    paddingInlineStart: 12,
    paddingInlineEnd: 10,
  },
  selectOpen: {
    backgroundColor: color.neutral5,
  },
  disabled: {
    backgroundColor: color.neutral5,
    cursor: 'default',
    ':hover': {
      backgroundColor: color.neutral5,
    },
  },
  spacer: {
    width: 4,
    flexShrink: 0,
  },
});
